import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface TokenPayload {
  accessToken: string;
  refreshToken: string;
}

interface AuthState {
  // Auth tokens
  tokens: TokenPayload | null;
  // User info (can be expanded as needed)
  user: {
    id?: string;
    username?: string;
    phoneNumber?: string;
    email?: string;
    role?: string;
  } | null;
  // Saved credentials for auto-fill
  savedCredentials: {
    username: string;
    // We don't store the actual password for security reasons
  } | null;
  // Session preferences
  rememberMe: boolean;
  // Auth status
  isAuthenticated: boolean;

  // Actions
  setTokens: (tokens: TokenPayload | null, rememberMe?: boolean) => void;
  setUser: (user: AuthState["user"]) => void;
  saveCredentials: (username: string) => void;
  clearSavedCredentials: () => void;
  logout: () => void;
}

// Create auth store with persistence
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      tokens: null,
      user: null,
      savedCredentials: null,
      rememberMe: false,
      isAuthenticated: false,

      setTokens: (tokens, rememberMe = false) =>
        set((state) => {
          // If rememberMe is true and we have an username, save it
          if (rememberMe && state.user?.username) {
            return {
              tokens,
              rememberMe,
              isAuthenticated: !!tokens,
              savedCredentials: { username: state.user.username },
            };
          }
          return { tokens, rememberMe, isAuthenticated: !!tokens };
        }),

      setUser: (user) => set(() => ({ user })),

      saveCredentials: (username) =>
        set(() => ({
          savedCredentials: { username },
        })),

      clearSavedCredentials: () =>
        set(() => ({
          savedCredentials: null,
          rememberMe: false,
        })),

      logout: () =>
        set((state) => {
          // When logging out, keep savedCredentials if rememberMe was true
          if (state.rememberMe) {
            return {
              tokens: null,
              user: null,
              isAuthenticated: false,
              // Keep rememberMe and savedCredentials
            };
          }

          return {
            tokens: null,
            user: null,
            savedCredentials: null,
            isAuthenticated: false,
            rememberMe: false,
          };
        }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

// Helper functions to access auth state outside of React components
export const getAuthTokens = (): TokenPayload | null =>
  useAuthStore.getState().tokens;
export const isAuthenticated = (): boolean =>
  useAuthStore.getState().isAuthenticated;
export const getAccessToken = (): string | undefined =>
  useAuthStore.getState().tokens?.accessToken;
export const getRefreshToken = (): string | undefined =>
  useAuthStore.getState().tokens?.refreshToken;
export const logout = () => useAuthStore.getState().logout();
export const getSavedCredentials = () =>
  useAuthStore.getState().savedCredentials;
